import { notify } from '@/lib/toast';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, Plus, Download, Archive, Trash2, PenLine, MessageCircle, Loader2 } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { StatusBadge } from '@/components/ui/status-badge';
import { FilterChips } from '@/components/ui/filter-chips';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = ['DRAFT', 'ACTIVE', 'SIGNED', 'COMPLETED', 'CANCELLED'] as const;

const STATUS_LABEL_KEYS: Record<(typeof STATUS_OPTIONS)[number], string> = {
  DRAFT: 'statusDraft',
  ACTIVE: 'statusActive',
  SIGNED: 'statusSigned',
  COMPLETED: 'statusCompleted',
  CANCELLED: 'statusCancelled',
};

interface ContractRow {
  id: string;
  contractNumber: string;
  status: string;
  archivedAt?: string | null;
  customer?: { name: string } | null;
  guarantor?: { name: string } | null;
  totalAmount: number | string;
  contractDate: string;
  signedDocumentUrl?: string | null;
}

const emptyForm = () => ({
  customerId: '',
  customerName: '',
  guarantorId: '',
  installmentPlanId: '',
  totalAmount: undefined as number | undefined,
  downPayment: undefined as number | undefined,
  installmentAmount: undefined as number | undefined,
  numberOfInstallments: undefined as number | undefined,
  termsText: '',
  notes: '',
});

interface CustomerOption { id: string; name: string; phone?: string }
interface GuarantorOption { id: string; name: string }

export function ContractsPage() {
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('installments:create') || hasPermission('installments:*');
  const canUpdate = hasPermission('installments:update') || hasPermission('installments:*');
  const canDelete = hasPermission('installments:delete') || hasPermission('installments:*');
  const { t } = useTranslation('installments');
  const qc = useQueryClient();
  const navigate = useNavigate();

  const statusLabel = (s: string): string => {
    const key = STATUS_LABEL_KEYS[s as keyof typeof STATUS_LABEL_KEYS];
    return key ? t(key) : s;
  };

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [customerSearch, setCustomerSearch] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [signOpen, setSignOpen] = useState<string | null>(null);
  const [signedUrl, setSignedUrl] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['contracts', page, statusFilter],
    queryFn: () => api.get('/contracts', { params: { page, limit: 20, status: statusFilter || undefined } }).then((r) => r.data),
  });

  const { data: customerResults } = useQuery({
    queryKey: ['contract-customer-search', customerSearch],
    queryFn: () => api.get('/customers/search', { params: { q: customerSearch, limit: 10 } }).then((r) => r.data),
    enabled: createOpen && customerSearch.length >= 2,
  });

  const { data: guarantors } = useQuery({
    queryKey: ['contract-customer-guarantors', form.customerId],
    queryFn: () => api.get(`/customers/${form.customerId}/guarantors`).then((r) => r.data),
    enabled: createOpen && !!form.customerId,
  });

  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.post('/contracts', payload).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('contractsToastCreated'));
      setCreateOpen(false);
      setForm(emptyForm());
      setCustomerSearch('');
      qc.invalidateQueries({ queryKey: ['contracts'] });
    },
    onError: (err) => notify.error(err, t('contractsErrCreate')),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.patch(`/contracts/${id}`, { status }).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('contractsToastUpdated'));
      qc.invalidateQueries({ queryKey: ['contracts'] });
    },
    onError: (err) => notify.error(err, t('contractsErrUpdate')),
  });

  const signMutation = useMutation({
    mutationFn: ({ id, url }: { id: string; url: string }) => api.patch(`/contracts/${id}/signed-document`, { signedDocumentUrl: url }).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('contractsToastSigned'));
      setSignOpen(null);
      setSignedUrl('');
      qc.invalidateQueries({ queryKey: ['contracts'] });
    },
    onError: (err) => notify.error(err, t('contractsErrSign')),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/contracts/${id}/archive`).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('contractsToastArchived'));
      qc.invalidateQueries({ queryKey: ['contracts'] });
    },
    onError: (err) => notify.error(err, t('contractsErrArchive')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/contracts/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('contractsToastDeleted'));
      setConfirmDeleteId(null);
      qc.invalidateQueries({ queryKey: ['contracts'] });
    },
    onError: (err) => notify.error(err, t('contractsErrDelete')),
  });

  const shareMutation = useMutation({
    mutationFn: (id: string) =>
      api.post(`/whatsapp/contract/${id}`).then(
        (r) =>
          r.data as { sent: boolean; link?: string; missingConfig?: string; configPath?: string },
      ),
    onSuccess: (data) => {
      if (data.sent) {
        toast.success(t('contractsToastShared'));
        return;
      }
      if (data.link) window.open(data.link, '_blank', 'noopener');
      if (data.missingConfig && data.configPath) {
        toast((tt) => (
          <span className="flex items-center gap-2">
            {t('contractsToastWhatsappNotConfigured')}
            <button
              className="font-medium text-primary-text underline"
              onClick={() => { toast.dismiss(tt.id); navigate(data.configPath!); }}
            >
              {t('contractsToastConfigure')}
            </button>
          </span>
        ), { icon: '⚙️', duration: 6000 });
      } else {
        toast.success(t('contractsToastOpeningWhatsapp'));
      }
    },
    onError: (err) => notify.error(err, t('contractsErrShare')),
  });

  const handleDownloadPdf = async (id: string, contractNumber: string) => {
    try {
      const res = await api.get(`/contracts/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract-${contractNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('contractsErrPdf'));
    }
  };

  const handleCreate = () => {
    if (!form.customerId) {
      toast.error(t('contractsErrSelectCustomer'));
      return;
    }
    const payload: Record<string, unknown> = { customerId: form.customerId };
    if (form.guarantorId) payload.guarantorId = form.guarantorId;
    if (form.installmentPlanId.trim()) payload.installmentPlanId = form.installmentPlanId.trim();
    if (form.totalAmount !== undefined) payload.totalAmount = form.totalAmount;
    if (form.downPayment !== undefined) payload.downPayment = form.downPayment;
    if (form.installmentAmount !== undefined) payload.installmentAmount = form.installmentAmount;
    if (form.numberOfInstallments !== undefined) payload.numberOfInstallments = form.numberOfInstallments;
    if (form.termsText.trim()) payload.termsText = form.termsText.trim();
    if (form.notes.trim()) payload.notes = form.notes.trim();
    createMutation.mutate(payload);
  };

  const contracts: ContractRow[] = data?.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('contractsPageTitle')}
        subtitle={t('contractsPageSubtitle')}
        action={
          canCreate ? (
            <Button variant="gradient" onClick={() => { setForm(emptyForm()); setCustomerSearch(''); setCreateOpen(true); }}>
              <Plus className="me-1.5 h-4 w-4" /> {t('contractsNewContract')}
            </Button>
          ) : undefined
        }
      />

      <FilterChips
        chips={[{ key: '', label: t('contractsAllStatuses') }, ...STATUS_OPTIONS.map((s) => ({ key: s, label: statusLabel(s) }))]}
        value={statusFilter}
        onChange={(k) => { setStatusFilter(k); setPage(1); }}
        variant="outline"
        size="sm"
        wrap
      />

      {isLoading ? (
        <SkeletonTable cols={6} rows={6} />
      ) : contracts.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={t('contractsEmptyTitle')}
          description={t('contractsEmptyDescription')}
        />
      ) : (
        <div className="space-y-3">
          {contracts.map((c) => (
            <Card key={c.id}>
              <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium">{c.contractNumber}</span>
                    <StatusBadge status={c.status} label={statusLabel(c.status)} />
                    {c.archivedAt && <StatusBadge status="ARCHIVED" label={t('contractsArchived')} />}
                  </div>
                  <p className="text-sm">{c.customer?.name ?? t('contractsUnknownCustomer')}{c.guarantor ? ` · ${t('contractsGuarantorInline', { name: c.guarantor.name })}` : ''}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('contractsTotalLine', { amount: formatCurrency(Number(c.totalAmount)) })} · {formatDate(c.contractDate)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {canUpdate && (
                    <Dropdown
                      className="h-9 text-xs"
                      value={c.status}
                      onChange={(e) => statusMutation.mutate({ id: c.id, status: e.value })}
                      options={STATUS_OPTIONS.map((s) => ({ label: statusLabel(s), value: s }))}
                      optionLabel="label"
                      optionValue="value"
                    />
                  )}
                  <TableRowActions
                    actions={[
                      {
                        key: 'pdf',
                        label: t('contractsActionPdf'),
                        icon: Download,
                        onSelect: () => handleDownloadPdf(c.id, c.contractNumber),
                        primary: true,
                        labeled: true,
                      },
                      {
                        key: 'share',
                        label: t('contractsActionShare'),
                        icon: MessageCircle,
                        onSelect: () => shareMutation.mutate(c.id),
                        primary: true,
                        labeled: true,
                        disabled: shareMutation.isPending,
                      },
                      {
                        key: 'sign',
                        label: t('contractsActionSign'),
                        icon: PenLine,
                        onSelect: () => { setSignOpen(c.id); setSignedUrl(c.signedDocumentUrl ?? ''); },
                        permission: canUpdate,
                      },
                      {
                        key: 'archive',
                        label: t('contractsActionArchive'),
                        icon: Archive,
                        onSelect: () => archiveMutation.mutate(c.id),
                        permission: canUpdate && !c.archivedAt,
                      },
                      {
                        key: 'delete',
                        label: t('contractsActionDelete'),
                        icon: Trash2,
                        onSelect: () => setConfirmDeleteId(c.id),
                        variant: 'destructive',
                        permission: canDelete,
                      },
                    ]}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
          <PaginationBar
            page={page}
            pageSize={20}
            total={data?.meta?.total ?? 0}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* Create dialog */}
      <Dialog header={t('contractsCreateTitle')} visible={createOpen} onHide={() => setCreateOpen(false)} style={{ width: '560px' }} modal>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="custSearch">{t('contractsCustomerLabel')}</Label>
            {form.customerId ? (
              <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                <span>{form.customerName || t('contractsSelectedCustomer')}</span>
                <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, customerId: '', customerName: '', guarantorId: '' })}>{t('contractsChange')}</Button>
              </div>
            ) : (
              <>
                <Input id="custSearch" value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} placeholder={t('contractsCustomerSearchPlaceholder')} />
                {customerSearch.length >= 2 && (
                  <ul className="max-h-40 overflow-y-auto rounded-md border">
                    {(customerResults ?? []).length === 0 ? (
                      <li className="px-3 py-2 text-sm text-muted-foreground">{t('contractsNoCustomers')}</li>
                    ) : (
                      (customerResults ?? []).map((c: CustomerOption) => (
                        <li key={c.id}>
                          <button type="button" className="w-full px-3 py-2 text-start text-sm hover:bg-muted/50" onClick={() => setForm({ ...form, customerId: c.id, customerName: c.name, guarantorId: '' })}>
                            {c.name}{c.phone ? ` · ${c.phone}` : ''}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </>
            )}
          </div>

          {form.customerId && (
            <div className="space-y-2">
              <Label htmlFor="guarantor">{t('contractsGuarantorOptional')}</Label>
              <Dropdown
                inputId="guarantor"
                className="w-full"
                value={form.guarantorId}
                onChange={(e) => setForm({ ...form, guarantorId: e.value })}
                options={[
                  { label: t('contractsNone'), value: '' },
                  ...(guarantors ?? []).map((g: GuarantorOption) => ({ label: g.name, value: g.id })),
                ]}
                optionLabel="label"
                optionValue="value"
                placeholder={t('contractsNone')}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="planId">{t('contractsPlanIdLabel')}</Label>
            <Input id="planId" value={form.installmentPlanId} onChange={(e) => setForm({ ...form, installmentPlanId: e.target.value })} placeholder={t('contractsPlanIdPlaceholder')} />
            <p className="text-xs text-muted-foreground">{t('contractsPlanIdHint')}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="totalAmount">{t('contractsTotalAmountLabel')}</Label>
              <Input id="totalAmount" type="number" min="0" step="0.01" value={form.totalAmount ?? ''} onChange={(e) => setForm({ ...form, totalAmount: e.target.value ? Number(e.target.value) : undefined })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="downPayment">{t('contractsDownPaymentLabel')}</Label>
              <Input id="downPayment" type="number" min="0" step="0.01" value={form.downPayment ?? ''} onChange={(e) => setForm({ ...form, downPayment: e.target.value ? Number(e.target.value) : undefined })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="installmentAmount">{t('contractsInstallmentAmountLabel')}</Label>
              <Input id="installmentAmount" type="number" min="0" step="0.01" value={form.installmentAmount ?? ''} onChange={(e) => setForm({ ...form, installmentAmount: e.target.value ? Number(e.target.value) : undefined })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="numberOfInstallments">{t('contractsNumInstallmentsLabel')}</Label>
              <Input id="numberOfInstallments" type="number" min="0" step="1" value={form.numberOfInstallments ?? ''} onChange={(e) => setForm({ ...form, numberOfInstallments: e.target.value ? Number(e.target.value) : undefined })} />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="terms">{t('contractsTermsLabel')}</Label>
            <Textarea id="terms" value={form.termsText} onChange={(e) => setForm({ ...form, termsText: e.target.value })} placeholder={t('contractsTermsPlaceholder')} />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('contractsCancel')}</Button>
            <Button variant="gradient" onClick={handleCreate} disabled={createMutation.isPending || !form.customerId}>
              {createMutation.isPending ? t('contractsCreating') : t('contractsCreate')}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Attach signed document dialog */}
      <Dialog header={t('contractsSignTitle')} visible={!!signOpen} onHide={() => setSignOpen(null)} style={{ width: '440px' }} modal>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="signedUrl">{t('contractsSignUrlLabel')}</Label>
            <Input id="signedUrl" value={signedUrl} onChange={(e) => setSignedUrl(e.target.value)} placeholder={t('contractsSignUrlPlaceholder')} />
            <p className="text-xs text-muted-foreground">{t('contractsSignHint')}</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSignOpen(null)}>{t('contractsCancel')}</Button>
            <Button variant="gradient" disabled={!signedUrl.trim() || signMutation.isPending} onClick={() => signOpen && signMutation.mutate({ id: signOpen, url: signedUrl.trim() })}>
              {signMutation.isPending ? (
                <><Loader2 className="me-2 h-4 w-4 animate-spin" />{t('contractsSaving')}</>
              ) : t('contractsSave')}
            </Button>
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDeleteId}
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={() => confirmDeleteId && deleteMutation.mutate(confirmDeleteId)}
        title={t('contractsDeleteTitle')}
        message={t('contractsDeleteMessage')}
        confirmLabel={t('contractsDeleteConfirm')}
        variant="danger"
      />
    </div>
  );
}
